import React from "react";
import styled from "styled-components";
import TyreQuoteForm from "../components/TyreQuoteForm";

const PageHeader = styled.section`
  height: 300px;
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("/images/land_hdr_bg.webp");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  color: white;

  .tagline {
    font-size: 1.2rem;
    color: #ff6600;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 0.5rem;
    text-align: center;
    width: 100%;
  }

  h1 {
    color: white;
    font-size: 3rem;
    text-align: center;
    width: 100%;
  }

  @media (max-width: 768px) {
    height: 200px;

    h1 {
      font-size: 2rem;
    }
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: #0a0a0a;
`;

const TyreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  .tyre-card {
    background: #1a1a1a;
    border-radius: 10px;
    padding: 2rem;
    text-align: center;
    border: 1px solid #333;
    transition: all 0.3s ease;

    &:hover {
      border-color: #ff6600;
      box-shadow: 0 5px 20px rgba(255, 102, 0, 0.2);
    }

    .tyre-image {
      margin-bottom: 1.5rem;

      img {
        width: 100%;
        max-width: 200px;
        height: auto;
      }
    }

    .tyre-size {
      font-size: 1.1rem;
      font-weight: 600;
      color: #ff6600;

      span {
        color: #e0e0e0;
      }
    }
  }
`;

const DescriptionSection = styled.div`
  margin: 3rem 0;
  padding: 2rem;
  background: #111;
  border-radius: 10px;
  border: 1px solid #333;

  h2 {
    color: #ff6600;
    margin-bottom: 1.5rem;
    text-align: center;
  }

  p {
    color: #b0b0b0;
    line-height: 1.8;
    margin-bottom: 1rem;
    text-align: center;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;

  .feature-card {
    background: #1a1a1a;
    padding: 2rem;
    border-radius: 10px;
    border: 1px solid #333;
    text-align: center;
    transition: all 0.3s ease;

    &:hover {
      border-color: #ff6600;
      box-shadow: 0 5px 20px rgba(255, 102, 0, 0.2);
    }

    .feature-icon {
      width: 60px;
      height: 60px;
      background: #ff6600;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1rem;
      color: white;
      font-size: 1.5rem;
      font-weight: bold;
    }

    h3 {
      color: #ff6600;
      margin-bottom: 1rem;
    }

    p {
      color: #b0b0b0;
      line-height: 1.6;
    }
  }
`;

const LandluxTyres = () => {
  const tyreVariants = [
    {
      image: "/images/landlux_11r.webp",
      size: "11R22.5",
    },
    {
      image: "/images/landlux_295.webp",
      size: "295/80R22.5",
    },
    {
      image: "/images/landlux_275.webp",
      size: "275/70R22.5",
    },
  ];

  const features = [
    {
      icon: "💪",
      title: "Superior Durability",
      description: "Built to withstand the toughest road conditions with advanced rubber compounds and reinforced construction.",
    },
    {
      icon: "⚡",
      title: "Enhanced Performance",
      description: "Optimized tread patterns for superior traction, handling, and fuel efficiency in all weather conditions.",
    },
    {
      icon: "🛡️",
      title: "Safety First",
      description: "Advanced safety features including improved braking performance and resistance to punctures and cuts.",
    },
    {
      icon: "💰",
      title: "Cost Effective",
      description: "Excellent value for money with extended tyre life and reduced maintenance costs for fleet operators.",
    },
  ];

  return (
    <>
      <PageHeader>
        <div className="container">
          <div className="tagline">Built for the long haul</div>
          <h1>Landlux Tyres</h1>
        </div>
      </PageHeader>

      <ContentSection>
        <div className="container">
          <div>
            <DescriptionSection>
              <h2>Premium Quality Tyres for Heavy-Duty Applications</h2>
              <p>
                Landlux tyres are engineered for the demanding Australian road conditions, offering exceptional performance for trucks, buses, and
                heavy commercial vehicles. With advanced technology and superior materials, Landlux tyres deliver reliability, durability, and
                outstanding value.
              </p>
              <p>
                Our Landlux range features innovative tread designs, reinforced sidewalls, and premium rubber compounds that ensure maximum mileage
                and optimal performance across various terrains and weather conditions.
              </p>
            </DescriptionSection>

            <TyreGrid>
              {tyreVariants.map((tyre, index) => (
                <div
                  key={index}
                  className="tyre-card">
                  <div className="tyre-image">
                    <img
                      src={tyre.image}
                      alt={`Landlux ${tyre.size}`}
                    />
                  </div>
                  <div className="tyre-size">
                    <span>Size:- </span>
                    {tyre.size}
                  </div>
                </div>
              ))}
            </TyreGrid>

            <section style={{ marginTop: 20 }}>
              <h2 style={{ color: "#ff6600", textAlign: "center" }}>Tyre Sizes & Description</h2>
              <div style={{ maxWidth: 700, margin: "12px auto 0", color: "#e0e0e0" }}>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, borderTop: "1px solid #333" }}>
                  <li style={{ padding: "12px 8px", borderBottom: "1px solid #333" }}>295 (Steer)</li>
                  <li style={{ padding: "12px 8px", borderBottom: "1px solid #333" }}>385 (Steer)</li>
                  <li style={{ padding: "12px 8px", borderBottom: "1px solid #333" }}>11R (Drive Tyre)</li>
                  <li style={{ padding: "12px 8px", borderBottom: "1px solid #333" }}>11R (All Position)</li>
                  <li style={{ padding: "12px 8px", borderBottom: "1px solid #333" }}>11R (Trailer Tyre)</li>
                  <li style={{ padding: "12px 8px" }}>275/70 R22.5</li>
                </ul>
              </div>
            </section>

            <FeaturesGrid>
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="feature-card">
                  <div className="feature-icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </FeaturesGrid>

            <TyreQuoteForm tyreType="Landlux" />
          </div>
        </div>
      </ContentSection>
    </>
  );
};

export default LandluxTyres;
